import { asArray, asBoolean, asInteger, asRecord, asString, assertUniqueIds } from "../document/validate.ts";
import type { TileRef } from "../document/types.ts";
import { validateZoneTypes, type CollisionBrush, type DaedalusGameAdapter, type ZoneTypeDefinition } from "./adapter.ts";
import { validateFootprintRules, type FootprintPolicy, type FootprintRule, type FootprintRuleMatch } from "./footprint-policy.ts";
import { validateObjectType, type ObjectFootprint, type ObjectPresence, type ObjectState, type ObjectTransition, type ObjectTypeDefinition } from "./objects.ts";
import { plainZoneStamp, readZoneStamp } from "./contract-zone-stamp.ts";
import { plainFieldGroups, readFieldGroups } from "./contract-field-groups.ts";
import { plainSchema, readSchema } from "./contract-schema.ts";
import { readRoomFields, roomFieldsOf, type AtmosphereLists } from "./room-registration.ts";
import { validateEntityType, validateReferenceTargets, validateSchema, type EntityTypeDefinition, type JsonSchema } from "./schema.ts";

/**
 * The game contract (ADR-052): the data half of a game's adapter as one document, so the editor and the
 * compiler read the same declarations without sharing a build. The shape is Daedalus's; the values are
 * the game's. The adapter's hooks (`collision`, `validate`, `propGround`, `propNoFade`, `onSave`, `onPlaytest`) stay in
 * code and are not part of the document: collision rules name terrain ids a contract cannot know, and are
 * derived from a world's terrains at compile time.
 */

/**
 * Version of the contract's own layout, raised only when an older reader would fail. Version 3 carries
 * each object type's transitions (ADR-058), which a version 2 reader would drop. A type's `presence` (ADR-058
 * amendment, P1.3-T4) is optional and a reader built before it ignores it, drawing such a prop as scenery,
 * so it left the layout at 3, as did the optional `mapSchema` (ADR-052's amendment of 2026-09-26), which a reader
 * built before it ignores, showing a map's name and size alone, and the optional `zoneTypes` (ADR-052's
 * amendment "zone types"), which a reader built before it ignores, having no zone layer to paint with, and a
 * zone type's optional `description` and `unpainted` (ADR-054's amendment of 2026-10-01), which such a reader
 * ignores, showing no tooltip and no unpainted line, and an object type's optional `mustBlock` (ADR-058's amendment
 * of 2026-10-04), which such a reader ignores, warning where a newer one refuses, and an object type's optional
 * `uninterruptible` (ADR-058's amendment of 2026-10-07), which such a reader ignores, taking a verb mid-strip as it
 * always did, and an object type's optional `zoneStamp` (ADR-096 decision 38), which such a reader ignores, offering
 * no zone to paint around a prop, and an object type's optional `soundSetField` and `choiceLabels` (ADR-058's
 * amendment of 2026-10-13), which such a reader ignores, compiling no sound set into an appearance and showing a
 * choice's options unlabelled, and an object type's optional `fieldGroups` (ADR-058's amendment of 2026-10-31), which such a
 * reader ignores, drawing every field in one list with no presets, and an object type's optional `hiddenOnMap` (ADR-100's amendment of 2026-10-14,
 * decision 57), which such a reader ignores, listing every thing of the type as a landmark, and an entity type's optional
 * `retired`, which such a reader ignores, offering the type for placing as it always did, and the optional
 * `roomZone`, `atmosphere` and `arrivalType` (ADR-122 decisions 2.2, 3.1 and 6), which such a reader ignores,
 * painting no floor in a new room, refusing every atmosphere name at compile and taking any entity as a host's
 * arrival, and the optional `doorFields` (ADR-122 decision 2.1), which such a reader ignores, editing no door
 * setting and compiling a door with the fields it holds.
 */
export const CONTRACT_SCHEMA_VERSION = 3;

/**
 * The contract document.
 */
export interface GameContract {
  readonly schemaVersion: number;
  readonly entityTypes: readonly EntityTypeDefinition[];
  readonly objectTypes: readonly ObjectTypeDefinition[];
  readonly collisionBrushes: readonly CollisionBrush[];
  readonly footprintPolicy: readonly FootprintRule[];
  readonly mapSchema?: JsonSchema;
  readonly zoneTypes?: readonly ZoneTypeDefinition[];
  readonly roomZone?: string;
  readonly atmosphere?: AtmosphereLists;
  readonly arrivalType?: string;
  readonly doorFields?: JsonSchema;
}

const INDENT = 2;
const MAX_BYTE = 255;
const MATCH_FIELDS = ["nameContains", "groupContains", "tilesetContains"] as const;

/**
 * The contract a game's adapter publishes.
 *
 * @param adapter The game's adapter.
 * @returns The document, with empty lists for what the game does not declare, and no map schema or zone types
 *   when it registers none.
 */
export function contractOf(adapter: DaedalusGameAdapter): GameContract {
  return {
    schemaVersion: CONTRACT_SCHEMA_VERSION,
    entityTypes: adapter.entityTypes,
    objectTypes: adapter.objectTypes ?? [],
    collisionBrushes: adapter.collisionBrushes ?? [],
    footprintPolicy: adapter.footprintPolicy ?? [],
    ...(adapter.mapSchema === undefined ? {} : { mapSchema: adapter.mapSchema }),
    ...zoneTypesOf(adapter.zoneTypes),
    ...roomFieldsOf(adapter),
    ...(adapter.doorFields === undefined ? {} : { doorFields: adapter.doorFields }),
  };
}

/**
 * Zone types as a field to spread: present only when one or more is registered, so a game that registers none
 * serialises the contract it did.
 *
 * @param types The types, or undefined.
 * @returns `{ zoneTypes }` or nothing.
 */
function zoneTypesOf(types: readonly ZoneTypeDefinition[] | undefined): { zoneTypes?: readonly ZoneTypeDefinition[] } {
  return types === undefined || types.length === 0 ? {} : { zoneTypes: types };
}

/**
 * The adapter a served contract makes: everything the document carries, with the footprint policy as a
 * function over its rules. A caller that also needs the game's hooks spreads this over the built-in
 * adapter.
 *
 * @param contract The contract.
 * @returns An adapter carrying only the contract's declarations.
 */
export function adapterFromContract(contract: GameContract): DaedalusGameAdapter {
  return { entityTypes: contract.entityTypes, objectTypes: contract.objectTypes, collisionBrushes: contract.collisionBrushes, footprintPolicy: contract.footprintPolicy, ...(contract.mapSchema === undefined ? {} : { mapSchema: contract.mapSchema }), ...zoneTypesOf(contract.zoneTypes), ...roomFieldsOf(contract), ...(contract.doorFields === undefined ? {} : { doorFields: contract.doorFields }) };
}

/**
 * Serialises a contract with fixed key order, so equal contracts give equal text and a version hash over
 * it is stable. A map schema follows the footprint policy only when the contract carries one, so a game that
 * registers none writes the text it wrote before.
 *
 * @param contract The contract.
 * @returns Pretty-printed JSON.
 */
export function serialiseContract(contract: GameContract): string {
  const plain = {
    schemaVersion: contract.schemaVersion,
    entityTypes: contract.entityTypes.map((type) => ({
      type: type.type,
      label: type.label,
      serverOnly: type.serverOnly,
      ...(type.retired === true ? { retired: true } : {}),
      ...(type.icon === undefined ? {} : { icon: plainFrame(type.icon) }),
      schema: plainSchema(type.schema),
    })),
    objectTypes: contract.objectTypes.map((type) => ({
      type: type.type,
      label: type.label,
      states: type.states.map((state) => ({ id: state.id, footprint: plainFootprint(state.footprint) })),
      initial: type.initial,
      ...(type.presence === undefined ? {} : { presence: { inside: type.presence.inside } }),
      ...(type.mustBlock === true ? { mustBlock: true } : {}),
      ...(type.uninterruptible === true ? { uninterruptible: true } : {}),
      ...(type.zoneStamp === undefined ? {} : { zoneStamp: plainZoneStamp(type.zoneStamp) }),
      ...(type.soundSetField === undefined ? {} : { soundSetField: type.soundSetField }),
      ...(type.hiddenOnMap === true ? { hiddenOnMap: true } : {}),
      ...(type.fieldGroups === undefined || type.fieldGroups.length === 0 ? {} : { fieldGroups: plainFieldGroups(type.fieldGroups) }),
      ...(type.choiceLabels === undefined ? {} : { choiceLabels: Object.fromEntries(Object.entries(type.choiceLabels).map(([field, labels]) => [field, [...labels]])) }),
      transitions: type.transitions.map(plainTransition),
      schema: plainSchema(type.schema),
    })),
    collisionBrushes: contract.collisionBrushes.map((brush) => ({ name: brush.name, value: brush.value })),
    footprintPolicy: contract.footprintPolicy.map((rule) => ({ match: plainMatch(rule.match), policy: plainPolicy(rule.policy) })),
    ...(contract.mapSchema === undefined ? {} : { mapSchema: plainSchema(contract.mapSchema) }),
    ...(contract.zoneTypes === undefined || contract.zoneTypes.length === 0 ? {} : { zoneTypes: contract.zoneTypes.map(plainZoneType) }),
    ...roomFieldsOf(contract),
    ...(contract.doorFields === undefined ? {} : { doorFields: plainSchema(contract.doorFields) }),
  };
  return JSON.stringify(plain, null, INDENT);
}

/**
 * A transition with fixed key order, omitting the optional keys it does not carry: `reverse` is written only
 * for a transition that plays its strip backwards (ADR-058's amendment of 2026-10-03), so a contract with
 * none serialises as it always did.
 *
 * @param transition The transition.
 * @returns The plain object.
 */
function plainTransition(transition: ObjectTransition): Record<string, unknown> {
  return {
    verb: transition.verb,
    from: transition.from,
    to: transition.to,
    ...(transition.requires === undefined ? {} : { requires: [...transition.requires] }),
    ...(transition.plays === undefined ? {} : { plays: transition.plays }),
    ...(transition.reverse === true ? { reverse: true } : {}),
  };
}

/**
 * The layout version contract text names, read without validating the rest of the document, so a reader
 * can tell a contract written for another build from a malformed one before parsing it.
 *
 * @param text The contract's text.
 * @returns The version, or null when the text is not JSON or names no integer version.
 */
export function contractSchemaVersionOf(text: string): number | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return null;
  }
  const version = (parsed as { schemaVersion?: unknown }).schemaVersion;
  return typeof version === "number" && Number.isInteger(version) ? version : null;
}

/**
 * A tile reference with fixed key order.
 *
 * @param frame The reference.
 * @returns The plain object.
 */
function plainFrame(frame: TileRef): { tileset: string; tile: number } {
  return { tileset: frame.tileset, tile: frame.tile };
}

/**
 * An authored footprint with fixed key order.
 *
 * @param footprint The footprint.
 * @returns The plain value.
 */
function plainFootprint(footprint: ObjectFootprint): "auto" | "none" | { cells: (readonly [number, number])[] } {
  return typeof footprint === "string" ? footprint : { cells: footprint.cells.map((cell) => [cell[0], cell[1]] as const) };
}

/**
 * A footprint policy with fixed key order.
 *
 * @param policy The policy.
 * @returns The plain value.
 */
function plainPolicy(policy: FootprintPolicy): "auto" | "none" | { band: number } {
  return typeof policy === "string" ? policy : { band: policy.band };
}

/**
 * A rule's match with its fields in a fixed order, omitting the ones it does not name.
 *
 * @param match The match.
 * @returns The plain object.
 */
function plainMatch(match: FootprintRuleMatch): Record<string, string> {
  const out: Record<string, string> = {};
  for (const field of MATCH_FIELDS) {
    const text = match[field];
    if (text !== undefined) {
      out[field] = text;
    }
  }
  return out;
}

/**
 * A contract of a layout this build does not read. `found` is the contract's layout version and `expected` this
 * build's, so a reader can tell a contract written for a newer build from an older one.
 */
export class ContractSchemaError extends Error {
  /**
   * @param found The contract's layout version.
   * @param expected The version this build reads.
   */
  public constructor(
    public readonly found: number,
    public readonly expected: number,
  ) {
    super(`schemaVersion ${found} is not ${expected}, which this build reads`);
    this.name = "ContractSchemaError";
  }
}

/**
 * Parses and validates contract text.
 *
 * @param text JSON text.
 * @returns The contract.
 * @throws SyntaxError when the text is not JSON; ContractSchemaError when the layout version is not the one
 *   this build reads; Error naming the offending path when a type, brush, rule or the map schema is malformed, or a type name
 *   repeats, or a reference field names a type the contract does not declare as one it can reach (ADR-052's
 *   amendment of 2026-09-27).
 */
export function deserialiseContract(text: string): GameContract {
  const record = asRecord(JSON.parse(text) as unknown, "contract");
  const schemaVersion = asInteger(record["schemaVersion"], "schemaVersion");
  if (schemaVersion !== CONTRACT_SCHEMA_VERSION) {
    throw new ContractSchemaError(schemaVersion, CONTRACT_SCHEMA_VERSION);
  }
  const entityTypes = asArray(record["entityTypes"], "entityTypes").map((item, i) => readEntityType(item, `entityTypes[${i}]`));
  const objectTypes = asArray(record["objectTypes"], "objectTypes").map((item, i) => readObjectType(item, `objectTypes[${i}]`));
  assertUniqueIds([...entityTypes.map((t) => t.type), ...objectTypes.map((t) => t.type)], "types");
  validateReferenceTargets(entityTypes, objectTypes);
  const collisionBrushes = asArray(record["collisionBrushes"], "collisionBrushes").map((item, i) => readBrush(item, `collisionBrushes[${i}]`));
  assertUniqueIds(collisionBrushes.map((b) => b.name), "collisionBrushes");
  const footprintPolicy = asArray(record["footprintPolicy"], "footprintPolicy").map((item, i) => readRule(item, `footprintPolicy[${i}]`));
  validateFootprintRules(footprintPolicy, "footprintPolicy");
  const mapSchema = record["mapSchema"] === undefined ? undefined : readSchema(record["mapSchema"], "mapSchema");
  if (mapSchema !== undefined) {
    validateSchema(mapSchema, "map schema");
  }
  const zoneTypes = record["zoneTypes"] === undefined ? [] : asArray(record["zoneTypes"], "zoneTypes").map((item, i) => readZoneType(item, `zoneTypes[${i}]`));
  validateZoneTypes(zoneTypes);
  const rooms = readRoomFields(record, zoneTypes.map((type) => type.id), entityTypes.map((type) => type.type));
  const doorFields = record["doorFields"] === undefined ? undefined : readSchema(record["doorFields"], "doorFields");
  if (doorFields !== undefined) {
    validateSchema(doorFields, "door fields");
  }
  return { schemaVersion, entityTypes, objectTypes, collisionBrushes, footprintPolicy, ...(mapSchema === undefined ? {} : { mapSchema }), ...zoneTypesOf(zoneTypes), ...rooms, ...(doorFields === undefined ? {} : { doorFields }) };
}

/**
 * A zone type with fixed key order: id, label, then the description and the unpainted mark only when it has
 * them, so a type with neither writes the text it did before they existed.
 *
 * @param type The zone type.
 * @returns The plain object.
 */
function plainZoneType(type: ZoneTypeDefinition): ZoneTypeDefinition {
  return { id: type.id, label: type.label, ...(type.description === undefined ? {} : { description: type.description }), ...(type.unpainted === true ? { unpainted: true } : {}) };
}

/**
 * Reads one zone type; the list is checked as a registration by `validateZoneTypes`. An `unpainted` of false
 * reads as absent, as the writer leaves it out.
 *
 * @param value The value.
 * @param path Path for messages.
 * @returns The zone type.
 * @throws Error naming the path when the id, the label or a description is not text, or an unpainted mark is
 *   not a boolean.
 */
function readZoneType(value: unknown, path: string): ZoneTypeDefinition {
  const record = asRecord(value, path);
  const description = record["description"] === undefined ? undefined : asString(record["description"], `${path}.description`);
  const unpainted = record["unpainted"] === undefined ? false : asBoolean(record["unpainted"], `${path}.unpainted`);
  return { id: asString(record["id"], `${path}.id`), label: asString(record["label"], `${path}.label`), ...(description === undefined ? {} : { description }), ...(unpainted ? { unpainted: true } : {}) };
}

/**
 * Reads one entity type and checks it against the editor's supported subset. `retired: false` reads as the key
 * left out, and a type-level key it does not know is ignored.
 *
 * @param value The value.
 * @param path Path for messages.
 * @returns The entity type.
 * @throws Error naming the path when a field is missing, `retired` is not a boolean or the schema is outside the
 *   subset.
 */
function readEntityType(value: unknown, path: string): EntityTypeDefinition {
  const record = asRecord(value, path);
  const definition: EntityTypeDefinition = {
    type: asString(record["type"], `${path}.type`),
    label: asString(record["label"], `${path}.label`),
    serverOnly: asBoolean(record["serverOnly"], `${path}.serverOnly`),
    ...(record["retired"] !== undefined && asBoolean(record["retired"], `${path}.retired`) ? { retired: true } : {}),
    ...(record["icon"] === undefined ? {} : { icon: readFrame(record["icon"], `${path}.icon`) }),
    schema: readSchema(record["schema"], `${path}.schema`),
  };
  validateEntityType(definition);
  return definition;
}

/**
 * Reads one object type and checks its states. `mustBlock: false` and `uninterruptible: false` each read as the key
 * left out, a `zoneStamp` is read when present, and a type-level key it does not know is ignored.
 *
 * @param value The value.
 * @param path Path for messages.
 * @returns The object type.
 * @throws Error naming the path when a state or transition is malformed, `mustBlock` or `uninterruptible` is not a boolean, the
 *   initial state is not among the states, or a transition leaves them.
 */
function readObjectType(value: unknown, path: string): ObjectTypeDefinition {
  const record = asRecord(value, path);
  const states: ObjectState[] = asArray(record["states"], `${path}.states`).map((item, i) => {
    const state = asRecord(item, `${path}.states[${i}]`);
    return { id: asString(state["id"], `${path}.states[${i}].id`), footprint: readFootprint(state["footprint"], `${path}.states[${i}].footprint`) };
  });
  const transitions = asArray(record["transitions"], `${path}.transitions`).map((item, i) => readTransition(item, `${path}.transitions[${i}]`));
  const presence = record["presence"] === undefined ? undefined : readPresence(record["presence"], `${path}.presence`);
  const mustBlock = record["mustBlock"] === undefined ? false : asBoolean(record["mustBlock"], `${path}.mustBlock`);
  const uninterruptible = record["uninterruptible"] === undefined ? false : asBoolean(record["uninterruptible"], `${path}.uninterruptible`);
  const zoneStamp = record["zoneStamp"] === undefined ? undefined : readZoneStamp(record["zoneStamp"], `${path}.zoneStamp`);
  const soundSetField = record["soundSetField"] === undefined ? undefined : asString(record["soundSetField"], `${path}.soundSetField`);
  const hiddenOnMap = record["hiddenOnMap"] === undefined ? false : asBoolean(record["hiddenOnMap"], `${path}.hiddenOnMap`);
  const choiceLabels = record["choiceLabels"] === undefined ? undefined : Object.fromEntries(Object.entries(asRecord(record["choiceLabels"], `${path}.choiceLabels`)).map(([field, labels]) => [field, asArray(labels, `${path}.choiceLabels.${field}`).map((label, i) => asString(label, `${path}.choiceLabels.${field}[${i}]`))]));
  const fieldGroups = record["fieldGroups"] === undefined ? undefined : readFieldGroups(record["fieldGroups"], `${path}.fieldGroups`);
  const definition: ObjectTypeDefinition = { type: asString(record["type"], `${path}.type`), label: asString(record["label"], `${path}.label`), states, initial: asString(record["initial"], `${path}.initial`), ...(presence === undefined ? {} : { presence }), ...(mustBlock ? { mustBlock: true as const } : {}), ...(uninterruptible ? { uninterruptible: true as const } : {}), ...(zoneStamp === undefined ? {} : { zoneStamp }), ...(soundSetField === undefined ? {} : { soundSetField }), ...(hiddenOnMap ? { hiddenOnMap: true as const } : {}), ...(choiceLabels === undefined ? {} : { choiceLabels }), ...(fieldGroups === undefined ? {} : { fieldGroups }), transitions, schema: readSchema(record["schema"], `${path}.schema`) };
  validateObjectType(definition);
  return definition;
}

/**
 * Reads a presence declaration (ADR-058 amendment, P1.3-T4). Its state is checked against the type's by
 * `validateObjectType`.
 *
 * @param value The value.
 * @param path Path for messages.
 * @returns The declaration.
 * @throws Error naming the path when it is not a record naming its inside state.
 */
function readPresence(value: unknown, path: string): ObjectPresence {
  const record = asRecord(value, path);
  return { inside: asString(record["inside"], `${path}.inside`) };
}

/**
 * Reads one transition. Its states, and that a reversed one names an animation, are checked against the type
 * by `validateObjectType`. `reverse: false` reads as the key left out.
 *
 * @param value The value.
 * @param path Path for messages.
 * @returns The transition.
 * @throws Error naming the path when a field is missing or of the wrong type.
 */
function readTransition(value: unknown, path: string): ObjectTransition {
  const record = asRecord(value, path);
  const requires = record["requires"] === undefined ? undefined : asArray(record["requires"], `${path}.requires`).map((item, i) => asString(item, `${path}.requires[${i}]`));
  const plays = record["plays"] === undefined ? undefined : asString(record["plays"], `${path}.plays`);
  const reverse = record["reverse"];
  if (reverse !== undefined && typeof reverse !== "boolean") {
    throw new Error(`${path}.reverse must be true or false`);
  }
  return {
    verb: asString(record["verb"], `${path}.verb`),
    from: asString(record["from"], `${path}.from`),
    to: asString(record["to"], `${path}.to`),
    ...(requires === undefined ? {} : { requires }),
    ...(plays === undefined ? {} : { plays }),
    ...(reverse === true ? { reverse: true as const } : {}),
  };
}

/**
 * Reads a tile reference.
 *
 * @param value The value.
 * @param path Path for messages.
 * @returns The reference.
 * @throws Error naming the path when a field is missing or the tile is not an integer.
 */
function readFrame(value: unknown, path: string): TileRef {
  const record = asRecord(value, path);
  return { tileset: asString(record["tileset"], `${path}.tileset`), tile: asInteger(record["tile"], `${path}.tile`) };
}

/**
 * Reads an authored footprint: `auto`, `none` or explicit `[column, rowsUp]` cells.
 *
 * @param value The value.
 * @param path Path for messages.
 * @returns The footprint.
 * @throws Error naming the path when the value is neither keyword nor a list of non-negative integer pairs.
 */
function readFootprint(value: unknown, path: string): ObjectFootprint {
  if (value === "auto" || value === "none") {
    return value;
  }
  const record = asRecord(value, path);
  const cells = asArray(record["cells"], `${path}.cells`).map((cell, i) => {
    const pair = asArray(cell, `${path}.cells[${i}]`);
    if (pair.length !== 2) {
      throw new Error(`${path}.cells[${i}] must be a [column, rowsUp] pair`);
    }
    return [asCellCoordinate(pair[0], `${path}.cells[${i}][0]`), asCellCoordinate(pair[1], `${path}.cells[${i}][1]`)] as const;
  });
  return { cells };
}

/**
 * Reads one cell coordinate.
 *
 * @param value The value.
 * @param path Path for messages.
 * @returns The coordinate.
 * @throws Error naming the path when the value is not a non-negative integer.
 */
function asCellCoordinate(value: unknown, path: string): number {
  const number = asInteger(value, path);
  if (number < 0) {
    throw new Error(`${path} must not be negative`);
  }
  return number;
}

/**
 * Reads one collision brush.
 *
 * @param value The value.
 * @param path Path for messages.
 * @returns The brush.
 * @throws Error naming the brush when its value is not a byte.
 */
function readBrush(value: unknown, path: string): CollisionBrush {
  const record = asRecord(value, path);
  const brush = { name: asString(record["name"], `${path}.name`), value: asInteger(record["value"], `${path}.value`) };
  if (brush.value < 0 || brush.value > MAX_BYTE) {
    throw new Error(`collision brush ${brush.name} has value ${brush.value}, not a byte`);
  }
  return brush;
}

/**
 * Reads one footprint rule.
 *
 * @param value The value.
 * @param path Path for messages.
 * @returns The rule.
 * @throws Error naming the path when a match field is not text or the policy is not a keyword or a band.
 */
function readRule(value: unknown, path: string): FootprintRule {
  const record = asRecord(value, path);
  const matchRecord = asRecord(record["match"], `${path}.match`);
  const match: Record<string, string> = {};
  for (const field of MATCH_FIELDS) {
    if (matchRecord[field] !== undefined) {
      match[field] = asString(matchRecord[field], `${path}.match.${field}`);
    }
  }
  return { match, policy: readPolicy(record["policy"], `${path}.policy`) };
}

/**
 * Reads a footprint policy.
 *
 * @param value The value.
 * @param path Path for messages.
 * @returns The policy.
 * @throws Error naming the path when the value is neither keyword nor a band.
 */
function readPolicy(value: unknown, path: string): FootprintPolicy {
  if (value === "auto" || value === "none") {
    return value;
  }
  const record = asRecord(value, path);
  return { band: asInteger(record["band"], `${path}.band`) };
}
